/**
 * ResultSummary 컴포넌트
 * 페이지 채점 결과 요약 카드
 * - 페이지 타이틀
 * - 통계 (정답률, 획득 점수)
 * - 정답/오답/대기 문제 번호 목록
 */

import React, { useMemo } from 'react';
import { PixelCard } from '../common/PixelCard';
import { PageResult } from '../../types/result';

/**
 * ResultSummary Props
 */
interface ResultSummaryProps {
  result: PageResult;
}

/**
 * ResultSummary 컴포넌트
 * 레트로 게임 스타일의 결과 요약 카드
 */
export const ResultSummary: React.FC<ResultSummaryProps> = ({ result }) => {
  /**
   * 정답/오답/대기 문제 번호 배열 생성
   */
  const categorizedProblems = useMemo(() => {
    const correct: string[] = [];
    const wrong: string[] = [];
    const pending: string[] = [];

    // result.problems가 undefined일 경우 빈 배열 처리
    const problems = result.problems ?? [];

    problems.forEach((problem) => {
      if (problem.is_correct === true) {
        correct.push(problem.problem_number);
      } else if (problem.is_correct === false) {
        wrong.push(problem.problem_number);
      } else {
        // is_correct === null (채점 대기)
        pending.push(problem.problem_number);
      }
    });

    return { correct, wrong, pending };
  }, [result.problems]);

  return (
    <PixelCard variant="panel" padding="large">
      {/* 페이지 타이틀 */}
      <h2 className="font-pixel text-xl text-center text-[#9bbc0f] mb-6">
        📊 {result.page}페이지 채점 결과
      </h2>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* 정답률 */}
        <div className="bg-gray-900 p-4 rounded-sm border-2 border-gray-700">
          <p className="font-pixel text-xs text-gray-400 mb-1">정답률</p>
          <p className="font-pixel text-3xl text-[#9bbc0f] mb-1">
            {(result.accuracy ?? 0).toFixed(1)}%
          </p>
          <p className="font-pixel text-xs text-gray-500">
            {result.correct_problems ?? 0}/{result.submitted_problems ?? 0}
          </p>
        </div>

        {/* 획득 점수 */}
        <div className="bg-gray-900 p-4 rounded-sm border-2 border-gray-700">
          <p className="font-pixel text-xs text-gray-400 mb-1">획득 점수</p>
          <p className="font-pixel text-3xl text-[#9bbc0f] mb-1">
            {result.total_score ?? 0}
          </p>
          <p className="font-pixel text-xs text-gray-500">
            최고 {result.max_combo ?? 0}콤보
          </p>
        </div>
      </div>

      {/* 문제 분류 */}
      <div className="space-y-3">
        {/* 정답 */}
        {categorizedProblems.correct.length > 0 && (
          <div className="bg-green-900 bg-opacity-30 p-3 rounded-sm border-2 border-green-700">
            <p className="font-pixel text-sm text-green-400 mb-2">
              ✅ 정답 ({categorizedProblems.correct.length}문제)
            </p>
            <p className="font-pixel text-xs text-gray-300">
              {categorizedProblems.correct.join(', ')}
            </p>
          </div>
        )}

        {/* 오답 */}
        {categorizedProblems.wrong.length > 0 && (
          <div className="bg-red-900 bg-opacity-30 p-3 rounded-sm border-2 border-red-700">
            <p className="font-pixel text-sm text-red-400 mb-2">
              ❌ 오답 ({categorizedProblems.wrong.length}문제)
            </p>
            <p className="font-pixel text-xs text-gray-300">
              {categorizedProblems.wrong.join(', ')}
            </p>
          </div>
        )}

        {/* 채점 대기 (T타입) */}
        {categorizedProblems.pending.length > 0 && (
          <div className="bg-yellow-900 bg-opacity-30 p-3 rounded-sm border-2 border-yellow-700">
            <p className="font-pixel text-sm text-yellow-400 mb-2">
              ⏳ 채점 대기 ({categorizedProblems.pending.length}문제)
            </p>
            <p className="font-pixel text-xs text-gray-300">
              {categorizedProblems.pending.join(', ')}
            </p>
          </div>
        )}
      </div>
    </PixelCard>
  );
};
